<template>
  <div class="triggers">
    <IslandBadge type="trigger" :draggable="false"></IslandBadge>
    <div class="triggers-contents">
      <div class="triggers-summary">
        <div class="proactive-trigger-message" v-if="shouldDisplayProactiveTriggerMessage()">
          <AlertOutline class="icon-1-5x" />
          <p>This flow will not be triggered on page load because the workspace has a proactive trigger limit of 0</p>
        </div>
        <div class="transition" v-for="(trigger, index) in currentFlow.triggers" :key="index">
          <div class="flow-trigger-box">
            <div class="trigger-option-icon">
              <span class="icon">
                <svg v-if="getIconName(trigger).startsWith('#')" class="custom-icon">
                  <use xmlns:xlink="http://www.w3.org/1999/xlink" :xlink:href="getIconName(trigger)"></use>
                </svg>
                <font-awesome-icon
                  class="fa-icons"
                  v-if="!getIconName(trigger).startsWith('#')"
                  :icon="['far', getIconName(trigger)]"
                ></font-awesome-icon>
              </span>
            </div>
            <div class="trigger-option-description">
              <span>
                {{ getSelectedTriggerDescription(trigger) }}
              </span>
            </div>
            <div class="edit-icon" v-if="isValidIntentSelected(trigger)">
              <EditIcon class="icon-1-2x" @click.stop="editIntent(trigger)"></EditIcon>
            </div>
          </div>
        </div>

        <div v-if="!isCurrentFlowTriggerable" class="none-option-description">None</div>

        <div class="transition" v-for="linkedFlow in parentFlows" :key="linkedFlow.id">
          <TransitionPicker :transition="getFlowTransition(linkedFlow)" :fixed="true"></TransitionPicker>
        </div>

        <div class="transition" v-for="parentAnswer in parentAnswers" :key="parentAnswer.id">
          <TransitionPicker :transition="getAnswerTransition(parentAnswer)" :fixed="true"></TransitionPicker>
        </div>
      </div>
    </div>

    <EditIntentModal
      v-if="showModal"
      :intent-id="intentToEdit"
      @closed="showModal = false"
      @completed="showModal = false"
      @failed-to-load="showModal = false"
    />
  </div>
</template>

<script lang="ts" setup>
import IslandBadge from './IslandBadge.vue';
import { AvailableTriggers, FlowViewModel, TriggerType } from '@/features/bots/models/designer';
import { ConditionGroupViewModel } from '@/shared/components/conditions/models/conditionGroupViewModel';
import EditIcon from 'vue-material-design-icons/SquareEditOutline.vue';
import EditIntentModal from '@/features/bots/components/designer/EditIntentModal.vue';
import { ParentViewModel } from '@/features/bots/models/parentViewModel';
import TransitionPicker from '@/features/bots/components/designer/TransitionPicker.vue';
import { TransitionType } from '@/features/bots/models/designer/transitionType';
import { TransitionViewModel } from '@/features/bots/models/designer/transitionViewModel';
import { TriggerItem } from '@/features/bots/models/designer/triggerType';
import { ConditionGroup } from '@/shared/components/conditions/models/conditionGroup';
import { UsageLimitType } from '@/features/settings/models/usageLimitType';
import AlertOutline from 'vue-material-design-icons/AlertOutline.vue';
import { computed, PropType, ref } from 'vue';
import { useStore } from 'vuex';
import { TriggerConditionAttributeType } from '@/features/bots/components/designer/Conditions/useTriggerConditions';

const props = defineProps({
  conditions: {
    type: Object as PropType<ConditionGroupViewModel>,
    required: true,
  },
});

const store = useStore();
const parentFlows = computed(() => store.getters.parentFlows);
const parentAnswers = computed(() => store.getters.parentAnswers);
const currentFlow = computed(() => store.getters.currentFlow);
const isCurrentFlowTriggerable = computed(() => store.getters.isCurrentFlowTriggerable);
const getIntentName = computed(
  () =>
    (intentId: string): string =>
      store.getters.getIntentName(intentId),
);
const workspaceUsageLimit = computed(
  () =>
    (feature: UsageLimitType): number =>
      store.getters.workspaceUsageLimit(feature),
);
const proactiveTriggerLimit = computed(() => workspaceUsageLimit.value(UsageLimitType.ProactiveTriggers));

const intentToEdit = ref('');
const showModal = ref(false);

function getFlowTransition(flow: ParentViewModel) {
  return {
    payload: flow.id,
    type: TransitionType.Flow,
  } as TransitionViewModel;
}

function getAnswerTransition(answer: ParentViewModel) {
  return {
    payload: answer.id,
    type: TransitionType.Answer,
  } as TransitionViewModel;
}

function getSelectedTriggerDescription(type: string) {
  const selected = AvailableTriggers.get(type);
  if (selected) {
    if (selected.type === TriggerType.MessageReceived && props.conditions) {
      return getDescriptionForMessageReceived(selected);
    }
    return selected.description;
  }
}

function getIconName(iconKey: string) {
  return AvailableTriggers.get(iconKey).icon;
}

function editIntent(trigger: string) {
  const intent = getIntentCondition(trigger);
  if (intent) {
    intentToEdit.value = intent.value;
    showModal.value = true;
  }
}

function isValidIntentSelected(trigger: string) {
  const intentCondition = getIntentCondition(trigger);
  return !!intentCondition && !!getIntentName.value(intentCondition.value);
}

function shouldDisplayProactiveTriggerMessage() {
  return (
    proactiveTriggerLimit.value === 0 &&
    currentFlow.value.triggers.some((trigger: string) => {
      return trigger === TriggerType.WidgetLoaded;
    })
  );
}

function getIntentCondition(trigger: string) {
  const selected = AvailableTriggers.get(trigger);
  if (selected) {
    if (selected.type === TriggerType.MessageReceived && props.conditions) {
      return ConditionGroup.find(
        props.conditions,
        (c) => c.attribute === TriggerConditionAttributeType.MessageIntent && c.value.length > 0,
      );
    }
  }
}

function getDescriptionForMessageReceived(selected: TriggerItem) {
  let output = '';
  const conditions = ConditionGroup.findAll(
    props.conditions,
    (c) =>
      (c.attribute === TriggerConditionAttributeType.MessageText ||
        c.attribute === TriggerConditionAttributeType.MessageIntent) &&
      c.value.length > 0,
  );
  for (const condition of conditions) {
    if (condition.type === 'intent') {
      output += ` ${getIntentName.value(condition.value) || condition.value}`;
    } else {
      output += ` ${condition.value}`;
    }
  }
  return output ? output : selected.description;
}
</script>

<style scoped lang="scss">
.edit-icon {
  background: $color-netcall-deep-sea;
  width: 35px;
  height: 100%;
  position: absolute;
  right: 0;
  color: $color-netcall-jonquil;

  &:hover {
    background: $color-netcall-jonquil;
    color: $color-netcall-deep-sea;
    cursor: pointer;
  }

  .icon-1-2x {
    position: absolute;
    top: 6px;
    left: 0;
    right: 0;
    margin: auto;
  }
}

.triggers {
  display: flex;
  position: relative;
  min-height: 60px;
  background: #eadc8e;
  border-radius: 5px;
  box-shadow: 0 3px 8px 0 rgba(0, 0, 0, 0.1);
}

.flow-trigger-box {
  background-color: $color-netcall-jonquil;
  display: flex;
  flex: 1;
  cursor: default;
  position: relative;
  width: 184px;
}

.triggers-contents {
  flex: 1;
  display: flex;
  position: relative;
  padding: 0.5em;
}

.triggers-summary {
  align-self: center;
  display: flex;
  flex: 1;
  flex-wrap: wrap;
}

.proactive-trigger-message {
  margin: 0.3em 0.3em 0.3em 0.6em;
  font-size: 0.875rem;
  display: flex;
  gap: 1rem;
  .material-design-icon {
    &__svg {
      position: relative;
      bottom: 0;
    }
  }
}

.transition {
  display: flex;
  flex-basis: 47%;
  justify-items: center;
  margin: 0.3em 0.3em 0.3em 0.6em;
}

.trigger-option-icon {
  padding: 5px 12px;
  display: flex;
  width: 30px;
  height: 100%;
  align-items: center;
}

.trigger-option-description {
  padding: 5px 10px;
  color: $color-netcall-deep-sea;
  position: relative;
  text-overflow: ellipsis;

  overflow: hidden;
  width: 70%;

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
.none-option-description {
  display: flex;
  justify-items: center;
  flex-basis: 45%;
  margin: 0.5em;
}

.icon {
  width: 1.1em;
  height: 1.1em;
  fill: $color-netcall-deep-sea;
  color: $color-netcall-deep-sea;
  .fa-icons {
    font-size: 1.2em;
  }
}
</style>
